// Every backend URL the app knows about, in one place.
// Import a resource and call .list() / .create() / .update(id) instead of
// building template strings inside components.

import api from './client';
import { getStoredUser } from '../utils/auth';
import type { CallOptions, QueryParams } from './client';
import type { ListResponse } from '../types/common';
import type {
  Family, Member,
  ChurchEvent, AttendanceRecord,
  Donation, Collection, Expense, Budget, BudgetTransaction,
  BudgetDocument, FinanceDashboard, FinanceSnapshot,
  PrintTemplate,
  Student, VisitRequest, PastoralVisit, CounselingSession, SickLog,
  AcademicYear, AcademicClass, SundayGrade, Teacher, SundayAttendance, SundayOffering,
  Exam, SundayExamMark,
  InfantBaptism, FirstCommunion, BaptismConfirmation, MarriageBann, MarriageCertificate,
  Missionary, MissionaryPrayerRequest, MissionaryVisitReport,
  StatutoryPayment, ChurchConfig, PublicBranding,
  CemeteryPlot, CemeteryBurial, CemeteryMaintenance,
  YouthMember, YouthProgram, YouthGroup,
  ChoirMember, ChoirSchedule, ChoirAsset,
  Asset, AssetAllocation, AssetMaintenance,
  Meeting, MeetingMinute, MeetingAction,
  PastorTenure, PastorPosting, PastorHandover,
  CommitteeTerm, CommitteeMember, SecretaryHandover,
  SystemUser, SystemRole, LoginResponse
} from '../types';

/**
 * A typed CRUD resource. `T` is what the endpoint returns and accepts.
 * Payloads are `Partial<T>` because modules build them field by field and
 * the server fills in the rest.
 */
export interface Resource<T> {
  path: string;
  /** `/api/members` or `/api/members/12`. */
  url: (id?: number | string) => string;
  list: (params?: QueryParams, options?: CallOptions) => Promise<ListResponse<T>>;
  get: (id: number | string, options?: CallOptions) => Promise<T>;
  create: (body: Partial<T>, options?: CallOptions) => Promise<T>;
  update: (id: number | string, body: Partial<T>, options?: CallOptions) => Promise<T>;
  patch: (id: number | string, body: Partial<T>, options?: CallOptions) => Promise<T>;
  remove: (id: number | string, options?: CallOptions) => Promise<void>;
}

// Every new record is stamped with the logged-in user's Full Name in `createdBy`,
// so the app records who created it without a field on any Add form. It is set
// only when the caller has not already provided one, and only on create — an
// update must not rewrite the original creator. (Simple/client-side by design:
// convenient for internal staff, not a security control.)
const stampCreator = <T>(body: Partial<T>): Partial<T> => {
  const name = (getStoredUser()?.name || '').trim();
  const b = body as Record<string, unknown>;
  if (!name || b.createdBy) return body;
  return { ...body, createdBy: name } as Partial<T>;
};

// Factory for the standard Spring Boot CRUD resource shape.
const resource = <T>(path: string): Resource<T> => ({
  path,
  url: (id) => (id === undefined ? `/api/${path}` : `/api/${path}/${id}`),
  list: (params, options) => api.get<ListResponse<T>>(`/api/${path}`, { ...options, params }),
  get: (id, options) => api.get<T>(`/api/${path}/${id}`, options),
  create: (body, options) => api.post<T>(`/api/${path}`, stampCreator(body), options),
  update: (id, body, options) => api.put<T>(`/api/${path}/${id}`, body, options),
  patch: (id, body, options) => api.patch<T>(`/api/${path}/${id}`, body, options),
  remove: (id, options) => api.del<void>(`/api/${path}/${id}`, options)
});

export const auth = {
  login: (username: string, password: string): Promise<LoginResponse> =>
    api.post<LoginResponse>('/api/auth/login', { username, password }, { skipAuth: true })
};

// --- People ---
export const families = resource<Family>('families');
export const members = resource<Member>('members');

// Server-side list: the backend sends one light page (basic columns, no photos)
// and the tab counts, so the client never downloads every record. The full
// record — photo, documents — is fetched by id with members.get(id) on view.
export const memberList = {
  /** Every member, light (basic columns only) — for the list, counts and links. */
  summary: (options?: CallOptions) =>
    api.get<Member[]>('/api/members/summary', options),
  page: (params?: QueryParams, options?: CallOptions) =>
    api.get<ListResponse<Member>>('/api/members/page', { ...options, params }),
  counts: (options?: CallOptions) =>
    api.get<Record<string, unknown>>('/api/members/counts', options),
  byFamily: (familyId: string, options?: CallOptions) =>
    api.get<Member[]>('/api/members/by-family', { ...options, params: { familyId } }),
  /**
   * Checks a member before saving, without saving. `errors` (duplicate Aadhaar
   * or Membership No.) stop the save; `warnings` (a mobile used in another
   * family, the same name and date of birth twice in one family) are shown
   * with a "Save anyway" choice. `id` is the member being edited.
   */
  duplicateCheck: (member: Partial<Member>, id?: number | null, options?: CallOptions) =>
    api.post<MemberDuplicateCheck>('/api/members/duplicate-check', member,
      { ...options, params: id ? { id } : undefined })
};

export interface DuplicateItem { field: string; message: string }
export interface MemberDuplicateCheck { errors: DuplicateItem[]; warnings: DuplicateItem[] }

// Bulk import for Members & Families. The browser reads the workbook and posts
// the rows, so the file itself never leaves the customer's machine — a 600-row
// register is a small JSON body, and nobody waits for an upload to find out
// row 22 is wrong.
//
// `check` is `run` with the writing switched off, so the preview the user
// approves is produced by the code that saves.
export const congregationImport = {
  check: (body: unknown, options?: CallOptions) =>
    api.post<ImportResult>('/api/import/congregation/check', body, options),
  run: (body: unknown, options?: CallOptions) =>
    api.post<ImportResult>('/api/import/congregation', body, options)
};

/** What the import did, or why it refused. Mirrors ImportResult on the server. */
export interface ImportProblem {
  sheet: string;
  row: number;
  field: string;
  message: string;
}

export interface ImportResult {
  ok: boolean;
  dryRun: boolean;
  familiesCreated: number;
  familiesUpdated: number;
  membersCreated: number;
  membersUpdated: number;
  problems: ImportProblem[];
  /** Held for approval (Settings › Approvals › Members & Families): nothing saved until approved. */
  approvalPending?: boolean;
  approvalRequestId?: number;
}

// --- Public family registration ---

/**
 * One person on the public registration form (head or member) — every field of
 * the office's Add Head / Add Member form except the (hidden) photo.
 */
export interface RegistrationPerson {
  /** Stable card id, so the server can keep masked Aadhaar / documents on resubmit. */
  key?: string;
  // Family & Status
  status?: string;
  dateOfDeath?: string;
  transferChurch?: string;
  memberStatus?: string;
  barcodeMembershipNo?: string;
  // Guest stay
  guestFrom?: string;
  guestUntil?: string;
  guestOrigin?: string;
  guestPurpose?: string;
  // Personal details
  name: string;
  relativeName?: string;
  relationToHead?: string;
  gender: string;
  location?: string;
  dateOfBirth: string;
  maritalStatus: string;
  dateOfMarriage?: string;
  bloodGroup?: string;
  education?: string;
  occupation?: string;
  mobileNumber?: string;
  /** 12 digits, or `XXXXXXXX1234` when shown back on the status page. */
  aadhaarNumber?: string;
  // Sacraments
  baptismDate?: string;
  confirmationDate?: string;
  communionStatus?: string;
  // Documents: file name + data URL (`KEEP` = already uploaded, not sent back)
  baptismCert?: string;
  baptismCertUrl?: string;
  marriageCert?: string;
  marriageCertUrl?: string;
  membershipCert?: string;
  membershipCertUrl?: string;
  idProof?: string;
  idProofUrl?: string;
}

/** The parish settings the public form needs, sent with the church's name. */
export interface RegistrationInfo {
  churchName: string;
  logo: string;
  /** Letterhead on the printed registration. */
  address?: string;
  contactNo?: string;
  documentUploads: boolean;
  memberStatuses: string[];
  guestPeriodMonths: number;
}

export interface RegistrationFormData {
  head: RegistrationPerson;
  address: string;
  parish?: string;
  email?: string;
  members: RegistrationPerson[];
}

export type RegistrationStatus = 'NEW' | 'ALREADY_MEMBER' | 'PENDING' | 'REJECTED' | 'APPROVED';

/** What the family sees after entering the head's mobile and date of birth. */
export interface RegistrationLookup {
  status: RegistrationStatus;
  referenceNo?: string;
  headName?: string;
  submittedAt?: string;
  updatedAt?: string;
  rejectReason?: string;
  familyId?: string;
  membershipNo?: string;
  approvedAt?: string;
  /** Whether the family may still change it (link on and not yet approved). */
  canEdit?: boolean;
  form?: RegistrationFormData;
}

/** A registration as the office sees it in the Pending Approval tab. */
export interface MemberRegistration {
  id: number;
  version: number;
  referenceNo: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  headName: string;
  headMobile: string;
  memberCount: number;
  submittedAt: string;
  updatedAt: string;
  editedByFamily: boolean;
  /** The office user who added it with Add Head; empty when it came from the public link. */
  submittedBy?: string | null;
  reviewedBy?: string;
  reviewedAt?: string;
  rejectReason?: string;
  familyId?: string;
  membershipNo?: string;
  /** Where it stands in Settings > Approvals: levels, who is next, history. */
  approval?: ApprovalView | null;
  /** Only on a single registration (get / update / approve / reject), not in the list. */
  form?: RegistrationFormData;
}

export interface RegistrationSettings {
  enabled: boolean;
  token: string;
}

/**
 * The login-free form. Every call carries the link's token; a closed link or a
 * stale token answers 404. `skipAuth` so a visitor is never bounced to login.
 */
export const publicRegistration = {
  info: (token: string) =>
    api.get<RegistrationInfo>(
      `/api/public/registration/${encodeURIComponent(token)}`, { skipAuth: true }),
  lookup: (token: string, mobile: string, dateOfBirth: string) =>
    api.post<RegistrationLookup>(
      `/api/public/registration/${encodeURIComponent(token)}/lookup`,
      { mobile, dateOfBirth }, { skipAuth: true }),
  /** The "Family Registration Form" print layout, or null when none is set up. */
  printTemplate: (token: string) =>
    api.get<PrintTemplate | null>(
      `/api/public/registration/${encodeURIComponent(token)}/print-template`, { skipAuth: true }),
  submit: (token: string, verifyMobile: string, verifyDob: string, form: RegistrationFormData, website = '') =>
    api.post<RegistrationLookup>(
      `/api/public/registration/${encodeURIComponent(token)}/submit`,
      { verifyMobile, verifyDob, form, website }, { skipAuth: true })
};

/** The office's review of new-family registrations (public link and Add Head). */
export const memberRegistrations = {
  list: (status: string, options?: CallOptions) =>
    api.get<MemberRegistration[]>('/api/member-registrations', { ...options, params: { status } }),
  counts: (options?: CallOptions) =>
    api.get<Record<string, number>>('/api/member-registrations/counts', options),
  get: (id: number, options?: CallOptions) =>
    api.get<MemberRegistration>(`/api/member-registrations/${id}`, options),
  /** Add Head from the office: the new family waits for the same approval as the public form. */
  create: (form: RegistrationFormData) =>
    api.post<MemberRegistration>('/api/member-registrations', form),
  update: (id: number, version: number, form: RegistrationFormData) =>
    api.put<MemberRegistration>(`/api/member-registrations/${id}`, { version, form }),
  approve: (id: number, version: number) =>
    api.post<MemberRegistration>(`/api/member-registrations/${id}/approve`, { version }),
  reject: (id: number, version: number, reason: string) =>
    api.post<MemberRegistration>(`/api/member-registrations/${id}/reject`, { version, reason })
};

/** Settings → Public Registration. */
export const registrationSettings = {
  get: (options?: CallOptions) => api.get<RegistrationSettings>('/api/registration-settings', options),
  setEnabled: (enabled: boolean) => api.put<RegistrationSettings>('/api/registration-settings', { enabled }),
  regenerate: () => api.post<RegistrationSettings>('/api/registration-settings/regenerate')
};

export const familyList = {
  page: (params?: QueryParams, options?: CallOptions) =>
    api.get<ListResponse<Family>>('/api/families/page', { ...options, params }),
  counts: (options?: CallOptions) =>
    api.get<Record<string, unknown>>('/api/families/counts', options)
};

// Server-paged read across donations + collections at once (a native UNION on
// the backend). Writes still go through `donations` / `collections`; this only
// feeds the merged Offerings list, its tab counts and its search.
export const offeringList = {
  page: (params?: QueryParams, options?: CallOptions) =>
    api.get<ListResponse<unknown>>('/api/offerings/page', { ...options, params }),
  counts: (options?: CallOptions) =>
    api.get<Record<string, unknown>>('/api/offerings/counts', options)
};

// Server-paged reads that UNION the register tables (banns+certificates;
// baptisms+communions+confirmations). Writes still go through the resources
// above.
export const marriageList = {
  page: (params?: QueryParams, options?: CallOptions) =>
    api.get<ListResponse<unknown>>('/api/marriages/page', { ...options, params }),
  counts: (options?: CallOptions) =>
    api.get<Record<string, unknown>>('/api/marriages/counts', options)
};
export const baptismList = {
  page: (params?: QueryParams, options?: CallOptions) =>
    api.get<ListResponse<unknown>>('/api/baptisms/page', { ...options, params }),
  counts: (options?: CallOptions) =>
    api.get<Record<string, unknown>>('/api/baptisms/counts', options)
};

// Birthdays / anniversaries computed on the server from the member register, so
// the browser fetches only the page it shows instead of every member.
export const celebrationList = {
  page: (params?: QueryParams, options?: CallOptions) =>
    api.get<ListResponse<unknown>>('/api/celebrations/page', { ...options, params })
};

// Server-paged event calendar, counts, distinct categories and the Summary
// aggregate — so the browser never downloads every event.
export const eventList = {
  page: (params?: QueryParams, options?: CallOptions) =>
    api.get<ListResponse<ChurchEvent>>('/api/events/page', { ...options, params }),
  counts: (options?: CallOptions) =>
    api.get<Record<string, unknown>>('/api/events/counts', options),
  categories: (options?: CallOptions) =>
    api.get<string[]>('/api/events/categories', options),
  summary: (options?: CallOptions) =>
    api.get<Record<string, unknown>>('/api/events/summary', options),
  /** Active events overlapping [from, to] — for the calendar's visible range. */
  range: (params?: QueryParams, options?: CallOptions) =>
    api.get<ChurchEvent[]>('/api/events/range', { ...options, params })
};

// Server-paged attendance log, tab counts, distinct events, and the Summary
// aggregate — so the browser never downloads every attendance record.
export const attendanceList = {
  page: (params?: QueryParams, options?: CallOptions) =>
    api.get<ListResponse<AttendanceRecord>>('/api/attendance/page', { ...options, params }),
  counts: (options?: CallOptions) =>
    api.get<Record<string, unknown>>('/api/attendance/counts', options),
  events: (options?: CallOptions) =>
    api.get<string[]>('/api/attendance/events', options),
  summary: (params?: QueryParams, options?: CallOptions) =>
    api.get<Record<string, unknown>>('/api/attendance/summary', { ...options, params })
};

// Server-paged expense ledger, counts, categories and the Summary aggregate.
export const expenseList = {
  page: (params?: QueryParams, options?: CallOptions) =>
    api.get<ListResponse<Expense>>('/api/expenses/page', { ...options, params }),
  counts: (options?: CallOptions) =>
    api.get<Record<string, unknown>>('/api/expenses/counts', options),
  categories: (options?: CallOptions) =>
    api.get<string[]>('/api/expenses/categories', options),
  summary: (params?: QueryParams, options?: CallOptions) =>
    api.get<Record<string, unknown>>('/api/expenses/summary', { ...options, params })
};

// --- Events & calendar ---
export const events = resource<ChurchEvent>('events');
export const attendance = resource<AttendanceRecord>('attendance');

// --- Finance ---
export const donations = resource<Donation>('donations');
export const collections = resource<Collection>('collections');
export const expenses = resource<Expense>('expenses');
/** Approval is its own call, never a field on update: the server stamps who and when. */
const approvable = <T>(path: string) => ({
  ...resource<T>(path),
  approve: (id: number | string, by: string, options?: CallOptions) =>
    api.put<T>(`/api/${path}/${id}/approve`, { by }, options),
  reject: (id: number | string, by: string, reason: string, options?: CallOptions) =>
    api.put<T>(`/api/${path}/${id}/reject`, { by, reason }, options)
});
export const budgets = approvable<Budget>('budgets');
export const budgetTransactions = approvable<BudgetTransaction>('budget-transactions');

/**
 * The hierarchical budget preparation document. Not a `resource()` because the
 * GET returns a year-keyed map and the save replaces the whole tree at once.
 */
export const budgetHeads = {
  path: 'budget-heads',
  url: () => '/api/budget-heads',
  get: (options?: CallOptions) => api.get<BudgetDocument>('/api/budget-heads', options),
  /** `loadedAt` is when the document was fetched; the server refuses a copy older than its last save. */
  save: (document: BudgetDocument, loadedAt?: string, options?: CallOptions) =>
    api.post<{ message: string; headsSaved: number }>('/api/budget-heads/save', document, {
      ...options, ...(loadedAt ? { params: { loadedAt } } : {})
    })
};

/** Read-only aggregation for the Financial Analytics screen. */
export interface FinanceDashboardQuery {
  finYear: string;
  period: string;
  /** Custom range; when both are given they replace the FY as the range. */
  from?: string;
  to?: string;
  /** Extra money sources to fold in: sundaySchool, transactions, payroll, statutory. */
  sources?: string[];
}

export const finance = {
  dashboard: ({ finYear, period, from, to, sources }: FinanceDashboardQuery, options?: CallOptions) =>
    api.get<FinanceDashboard>('/api/finance/dashboard', {
      ...options,
      params: {
        finYear, period,
        ...(from && to ? { from, to } : {}),
        ...(sources?.length ? { sources: sources.join(',') } : {})
      }
    }),
  snapshot: (options?: CallOptions) =>
    api.get<FinanceSnapshot>('/api/finance/snapshot', options)
};

// --- Sunday School ---
export const students = resource<Student>('students');
export const academicYears = resource<AcademicYear>('academic-years');
export const sundayGrades = resource<SundayGrade>('sunday-grades');
export const sundayClasses = resource<AcademicClass>('sunday-classes');
export const sundayTeachers = resource<Teacher>('sunday-teachers');
export const sundayAttendance = resource<SundayAttendance>('sunday-attendance');
export const sundayOfferings = resource<SundayOffering>('sunday-offerings');
export const sundayExams = resource<Exam>('sunday-exams');
export const sundayExamMarks = resource<SundayExamMark>('sunday-marks');

// --- Parish registers ---
export const infantBaptisms = resource<InfantBaptism>('infant-baptisms');
export const firstCommunions = resource<FirstCommunion>('first-communions');
export const baptismConfirmations = resource<BaptismConfirmation>('baptism-confirmations');
export const marriageBanns = resource<MarriageBann>('marriage-banns');
export const marriageCertificates = resource<MarriageCertificate>('marriage-certificates');

// --- Missionaries ---
export const missionaries = resource<Missionary>('missionaries');
export const missionaryPrayers = resource<MissionaryPrayerRequest>('missionary-prayers');
export const missionaryVisits = resource<MissionaryVisitReport>('missionary-visits');

// --- Statutory payments (institutional dues, not payroll deductions) ---
export const statutoryPayments = resource<StatutoryPayment>('statutory-payments');

/**
 * The church letterhead. A single pinned row, so it is a get/save pair rather
 * than a `resource()`. The logo is readable without a token because the login
 * screen renders it before anyone has signed in.
 */
export const churchConfig = {
  path: 'church-config',
  url: () => '/api/church-config',
  get: (options?: CallOptions) => api.get<ChurchConfig>('/api/church-config', options),
  save: (config: ChurchConfig, options?: CallOptions) =>
    api.post<ChurchConfig>('/api/church-config', config, options),
  publicLogo: (options?: CallOptions) =>
    api.get<{ logo: string }>('/api/church-config/public/logo', { ...options, skipAuth: true }),
  /** Logo, name and default language for the login screen — no token needed. */
  publicBranding: (options?: CallOptions) =>
    api.get<PublicBranding>('/api/church-config/public/branding', { ...options, skipAuth: true }),
  /** Replaces one dropdown list; the rest of the configuration is untouched. */
  saveOptionList: (key: string, values: string[], options?: CallOptions) =>
    api.put<ChurchConfig>(`/api/church-config/option-lists/${encodeURIComponent(key)}`, values, options),
  /** Family ID / Member ID / Membership No.: saved prefix and start, and the next number each will give. */
  idNumbers: (options?: CallOptions) =>
    api.get<IdNumberRow[]>('/api/church-config/id-numbers', options)
};

export interface IdNumberRow {
  kind: 'FAMILY' | 'MEMBER' | 'MEMBERSHIP';
  label: string;
  prefix: string;
  start: number;
  defaultPrefix: string;
  defaultStart: number;
  /** The highest ID in use with the saved prefix, e.g. "FAM-8682", or null. */
  usedUpTo: string | null;
  /** The number the next ID will get — already past every number in use or reserved. */
  nextNumber: number;
}

// --- Pastoral care ---
export const visitRequests = resource<VisitRequest>('visit-requests');
/** Visits actually carried out, as opposed to the mobile-app request inbox. */
export const pastoralVisits = resource<PastoralVisit>('pastoral-visits');
export const counselingSessions = resource<CounselingSession>('counseling-sessions');
export const sickLogs = resource<SickLog>('sick-logs');

// --- Cemetery ---
export const cemeteryPlots = resource<CemeteryPlot>('cemetery-plots');
export const cemeteryBurials = resource<CemeteryBurial>('cemetery-burials');
export const cemeteryMaintenance = resource<CemeteryMaintenance>('cemetery-maintenance');

// --- Youth ---
export const youthMembers = resource<YouthMember>('youth-members');
export const youthPrograms = resource<YouthProgram>('youth-programs');
export const youthGroups = resource<YouthGroup>('youth-groups');

// --- Choir ---
export const choirMembers = resource<ChoirMember>('choir-members');
export const choirSchedules = resource<ChoirSchedule>('choir-schedules');
export const choirAssets = resource<ChoirAsset>('choir-assets');

// --- Assets ---
export const assets = resource<Asset>('assets');
export const assetAllocations = resource<AssetAllocation>('asset-allocations');
export const assetMaintenance = resource<AssetMaintenance>('asset-maintenance');

// --- Meetings ---
export const meetingRecords = resource<Meeting>('meeting-records');
export const meetingMinutes = resource<MeetingMinute>('meeting-minutes');
export const meetingActions = resource<MeetingAction>('meeting-actions');

// --- History / governance ---
export const pastorTenures = resource<PastorTenure>('pastor-tenures');
export const pastorPostings = resource<PastorPosting>('pastor-postings');
export const pastorHandovers = resource<PastorHandover>('pastor-handovers');
export const committeeTerms = resource<CommitteeTerm>('committee-terms');
export const committeeMembers = resource<CommitteeMember>('committee-members');
export const secretaryHandovers = resource<SecretaryHandover>('secretary-handovers');

// --- Admin ---
export const systemUsers = resource<SystemUser>('system-users');
export const systemRoles = resource<SystemRole>('system-roles');

// --- Approvals (Settings > Approvals, and the inbox) ---

/** One entry in a request's history. */
export interface ApprovalStepView {
  level: number;
  /** CANCELLED: closed by the system (the record was deleted, or a newer edit replaced it). */
  decision: 'APPROVED' | 'REJECTED' | 'RESET' | 'CANCELLED' | 'WITHDRAWN';
  byName?: string;
  byUsername?: string;
  byRole?: string;
  at: string;
  comment?: string;
  /** An administrator acted past the usual rules. */
  override?: boolean;
}

/** An approval request as the signed-in user sees it. */
export interface ApprovalView {
  id: number;
  version: number;
  type: string;
  typeLabel: string;
  module: string;
  recordId: number;
  reference?: string;
  summary?: string;
  requestedBy?: string;
  requestedAt?: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED' | 'WITHDRAWN';
  levelsRequired: number;
  approvedLevels: number;
  nextLevel: number | null;
  /** Who may approve the next level (role names, or "Anyone with Full Access to ..."). */
  waitingFor: string[];
  levels: Array<{ level: number; approvers: string[] }>;
  steps: ApprovalStepView[];
  closedAt?: string;
  /** A closed request: who approved, rejected or withdrew it last, and their remark. */
  decidedBy?: string | null;
  decidedByRole?: string | null;
  decisionComment?: string | null;
  /** A new record waiting for approval: its submitted fields, for the approver to read. */
  details?: Array<{ field: string; value: string }>;
  /** Whether the signed-in user may approve or reject now, and if not, why. */
  canAct: boolean;
  whyNot?: string | null;
}

export type ApprovalBox = 'waiting' | 'mine' | 'history' | 'module';

/** One save inside a batch: an add (POST) or a change / delete / restore (PUT). */
export interface BatchOperation {
  method: 'POST' | 'PUT';
  path: string;
  body: object;
}

/** Saved now (`held: false`, with each saved record in order) or held as ONE approval request. */
export interface BatchResult {
  held: boolean;
  results?: unknown[];
  approvalRequestId?: number;
  message?: string;
}

/**
 * Several saves that belong to one action (promote a head, write off an asset,
 * a new pastor posting...) sent together: all saved at once, or — when any of
 * them needs approval — held as one request and applied together on the last
 * yes. A held batch brings the usual X-Approval notice.
 */
export const batch = {
  add: <T>(r: Resource<T>, body: Partial<T>): BatchOperation =>
    ({ method: 'POST', path: r.url(), body: stampCreator(body) as object }),
  change: <T>(r: Resource<T>, id: number | string, body: Partial<T>): BatchOperation =>
    ({ method: 'PUT', path: r.url(id), body: body as object }),
  run: (operations: BatchOperation[], summary: string, options?: CallOptions) =>
    api.post<BatchResult>('/api/batch', { summary, operations }, options)
};

export const approvals = {
  /** box "module" lists what waits in `module` (for its screen). */
  list: (box: ApprovalBox, options?: CallOptions, module?: string) =>
    api.get<ApprovalView[]>('/api/approvals', { ...options, params: module ? { box, module } : { box } }),
  /** The requester (or an administrator) takes a waiting request back. */
  withdraw: (id: number, comment?: string) =>
    api.post<ApprovalView>(`/api/approvals/${id}/withdraw`, { comment }),
  /** A rejected new record, corrected and sent again by its requester. */
  resubmit: (id: number, changes: Record<string, string>) =>
    api.post<ApprovalView>(`/api/approvals/${id}/resubmit`, changes),
  count: (options?: CallOptions) => api.get<{ waiting: number }>('/api/approvals/count', options),
  get: (id: number, options?: CallOptions) => api.get<ApprovalView>(`/api/approvals/${id}`, options),
  /** No version: approve whatever the request is now (the strip reads it just before). */
  approve: (id: number, version?: number, comment?: string) =>
    api.post<ApprovalView>(`/api/approvals/${id}/approve`, { version, comment }),
  reject: (id: number, version: number, reason: string) =>
    api.post<ApprovalView>(`/api/approvals/${id}/reject`, { version, comment: reason })
};

/** One approval type's settings, as Settings > Approvals shows them. */
export interface ApprovalPolicyView {
  type: string;
  label: string;
  module: string;
  minLevels: number;
  maxLevels: number;
  levels: number;
  /** Role ids per level: [[level 1], [level 2], [level 3]]; empty = Full Access holders. */
  levelRoles: number[][];
  adminOverride: boolean;
  /** e.g. "Level 2: no active user holds Treasurer." */
  warnings: string[];
  /** Add policies: what the module's "Add" approval covers and leaves out. */
  note?: string;
}

export const approvalPolicies = {
  list: (options?: CallOptions) => api.get<ApprovalPolicyView[]>('/api/approval-policies', options),
  save: (type: string, body: { levels: number; levelRoles: number[][]; adminOverride: boolean }) =>
    api.put<ApprovalPolicyView[]>(`/api/approval-policies/${encodeURIComponent(type)}`, body)
};

/**
 * Configured print layouts, plus the two reads a printing screen makes:
 * `resolve` for the template a document should use, `usable` for the list a
 * "choose a template" dialog offers. `remove` is a soft delete, so the
 * standard resource DELETE is replaced with one that carries a reason.
 */
export const printTemplates = {
  ...resource<PrintTemplate>('print-templates'),
  /** The default for this pair, or null when the parish has configured none. */
  resolve: (module: string, documentType: string, options?: CallOptions) =>
    api.get<PrintTemplate | null>('/api/print-templates/resolve', {
      ...options, params: { module, documentType }
    }),
  usable: (module: string, documentType: string, options?: CallOptions) =>
    api.get<PrintTemplate[]>('/api/print-templates/usable', {
      ...options, params: { module, documentType }
    }),
  setDefault: (id: number, options?: CallOptions) =>
    api.put<PrintTemplate>(`/api/print-templates/${id}/default`, {}, options),
  softDelete: (id: number, reason: string, options?: CallOptions) =>
    api.del<PrintTemplate>(`/api/print-templates/${id}`, {
      ...options, params: { reason }
    })
};

const endpoints = {
  auth,
  families, members,
  events, attendance,
  donations, collections, expenses, budgets, budgetTransactions,
  budgetHeads, finance,
  students, academicYears, sundayGrades, sundayClasses, sundayTeachers,
  sundayAttendance, sundayOfferings, sundayExams, sundayExamMarks,
  infantBaptisms, firstCommunions, baptismConfirmations, marriageBanns, marriageCertificates,
  missionaries, missionaryPrayers, missionaryVisits,
  statutoryPayments, churchConfig,
  visitRequests, pastoralVisits, counselingSessions, sickLogs,
  cemeteryPlots, cemeteryBurials, cemeteryMaintenance,
  youthMembers, youthPrograms,
  choirMembers, choirSchedules, choirAssets,
  assets, assetAllocations, assetMaintenance,
  meetingRecords, meetingMinutes, meetingActions,
  pastorTenures, pastorPostings, pastorHandovers,
  committeeTerms, committeeMembers, secretaryHandovers,
  systemUsers, systemRoles, printTemplates,
  batch
};

export default endpoints;
